import { MembersApi } from "@/api/members/MembersApi";
import { queryKeys } from "@/api/queryKeys";
import { useAuth } from "@/auth/AuthContext";
import type { OrgMembershipRole } from "@/auth/membershipRoles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

function useInvalidateMembers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? "";
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.members.all });
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.my(userId),
      });
    }
  };
}

export function useOrgMembers(orgId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id ?? "";
  return useQuery({
    queryKey: queryKeys.members.list(userId, orgId ?? ""),
    queryFn: () => MembersApi.listOrgMembers(orgId!),
    enabled: Boolean(userId && orgId),
  });
}

export function useOrgMemberDirectory(orgId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id ?? "";
  return useQuery({
    queryKey: queryKeys.members.directory(userId, orgId ?? ""),
    queryFn: () => MembersApi.listOrgMemberDirectory(orgId!),
    enabled: Boolean(userId && orgId),
  });
}

export function useSetMemberRoles(orgId: string | undefined) {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: { membershipId: string; roles: OrgMembershipRole[] }) => {
      if (!orgId) throw new Error("Organisation fehlt.");
      return MembersApi.setMemberRoles(input.membershipId, input.roles);
    },
    onSuccess: invalidate,
  });
}

export function useSetMemberStatus(orgId: string | undefined) {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (input: { membershipId: string; status: "active" | "disabled" }) => {
      if (!orgId) throw new Error("Organisation fehlt.");
      return MembersApi.setMemberStatus(input.membershipId, input.status);
    },
    onSuccess: invalidate,
  });
}
